const r = (v) => Math.round(v * 1000) / 1000;

/**
 * Maps an array of numeric values to SVG {x, y} coordinates.
 * @param {number[]} data
 * @param {{ width?: number, height?: number, padding?: number, domainMin?: number, domainMax?: number }} options
 *   domainMin/domainMax — fixed scale (e.g. 0/100 for percentages).
 *   If omitted, min/max are derived from the data (relative scale).
 * @returns {{ x: number, y: number }[]}
 */
export function buildSparklinePoints(
  data,
  { width = 120, height = 32, padding = 3, domainMin, domainMax } = {}
) {
  if (!data || data.length < 2) return [];

  const min = domainMin ?? Math.min(...data);
  const max = domainMax ?? Math.max(...data);
  const range = max - min || 1;
  const drawW = width - padding * 2;
  const drawH = height - padding * 2;

  return data.map((v, i) => ({
    x: r(padding + (i / (data.length - 1)) * drawW),
    y: r(height - padding - ((v - min) / range) * drawH),
  }));
}

/**
 * Builds SVG path strings for a sparkline line and its fill area.
 * Uses Catmull-Rom interpolation for smooth curves (same visual as Chart.js tension).
 * @param {{ x: number, y: number }[]} points
 * @param {number} baselineY - Y coordinate of the fill baseline
 * @returns {{ linePath: string, areaPath: string }}
 */
export function buildSparklinePaths(points, baselineY) {
  if (!points || points.length < 2) return { linePath: "", areaPath: "" };

  const n = points.length;
  let linePath = `M${points[0].x},${points[0].y}`;

  for (let i = 0; i < n - 1; i++) {
    const p0 = i === 0 ? points[0] : points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i + 2 < n ? points[i + 2] : points[n - 1];

    const cp1x = r(p1.x + (p2.x - p0.x) / 6);
    const cp1y = r(p1.y + (p2.y - p0.y) / 6);
    const cp2x = r(p2.x - (p3.x - p1.x) / 6);
    const cp2y = r(p2.y - (p3.y - p1.y) / 6);

    linePath += ` C${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }

  const last = points[n - 1];
  const first = points[0];
  const areaPath = `${linePath} L${last.x},${baselineY} L${first.x},${baselineY} Z`;

  return { linePath, areaPath };
}
